import { useMemo } from "react";
import { useRouter } from "next/router";
import { Col } from "antd";
import {
  CheckCircleFilled,
  CloseCircleFilled,
  EditFilled,
  FileTextOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import {
  CustomFieldConfig,
  CustomStepAssets,
  Flow,
} from "@dreambigger/shared/src/types";
import { useApplication, useDisclosureLogList } from "../../api";
// Separate `import type` rather than an inline `type` modifier: the repo's
// Prettier (2.3.0) predates that syntax and fails to parse it.
import { DISCLOSURE_DECISION_EVENT_TYPES } from "../../api/use-disclosure-logs";
import type { DisclosureLogRecord } from "../../api/use-disclosure-logs";
import disclosureDocumentUrl from "../../utils/disclosureDocumentUrl";
import toPlainText from "@dreambigger/shared/src/utils/toPlainText";

// `z` (the timezone abbreviation) needs advancedFormat, which needs utc+timezone.
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advancedFormat);

const isTruthy = (value: unknown): boolean =>
  value === true || value === "true";

interface AttestationSummaryProps {
  flow: Flow;
  applicationId: string;
  /**
   * The steps still in the applicant's effective path. Disclosures on steps that
   * were abandoned (a different branch was taken) are hidden, matching the step
   * review cards. Undefined means no path is being tracked, so nothing is filtered.
   */
  effectiveVisitedPath?: string[];
  /** Modern theme, so the outer padding matches the step review cards beside it. */
  isV2?: boolean;
}

interface AttestationEntry {
  key: string;
  stepSlug: string;
  agreed: boolean;
  fieldText: string | null;
  label: string | null;
  createdAt: string | null;
  ip: string | null;
  href?: string;
}

/**
 * Review-step attestation block, and the record of consent captured by the
 * review-step screenshot.
 *
 * Presence and decision come from the applicant's saved checkbox in meta.responses
 * — the functional truth, written server-side with every step and never dropped.
 * Each row is then enriched from the append-only disclosure log for the defensible
 * detail (server time, IP). This ordering is deliberate: if a client-fired log
 * event is ever lost, the disclosure still renders here (from the checkbox) rather
 * than silently vanishing from the artifact; only the log-backed detail degrades.
 *
 * Declined entries are shown rather than omitted — silence would be ambiguous
 * against a disclosure the flow never configured, which is the question an auditor
 * needs answered. The full agree language (from the field config) is always shown,
 * no truncation. Renders nothing when there are no disclosure decisions on path.
 */
export default function AttestationSummary({
  flow,
  applicationId,
  effectiveVisitedPath,
  isV2 = false,
}: AttestationSummaryProps) {
  const router = useRouter();
  const applicationHelper = useApplication(flow.financialInstitution.id);
  const { logs } = useDisclosureLogList(
    flow.financialInstitution.id,
    applicationId
  );

  // Disclosure fields per step, read from the flow config (the client owns field
  // shapes). Filtered to the visited path so abandoned branches drop out.
  //
  // NOTE: reads `skins[0]`, matching `review.tsx` (which builds every step-review
  // card off `flow.skins[0].steps`). The whole review step assumes skin[0], so this
  // is consistent — but on a genuinely multi-skin flow the rendered skin may not be
  // skin[0], and writes (step-wrapper passes the live `step.assets`) would disagree
  // with these reads. Fixing that means threading the rendered skin through the
  // review step generally, not just here; tracked as a separate multi-skin item.
  const disclosuresByStep = useMemo(() => {
    const inPath = (slug: string) =>
      !effectiveVisitedPath || effectiveVisitedPath.includes(slug);
    const steps = flow.skins?.[0]?.steps ?? [];
    return steps
      .filter((step) => !!step.slug && inPath(step.slug))
      .map((step) => ({
        stepSlug: step.slug,
        fields: (
          ((step.assets as CustomStepAssets)?.customFields ??
            []) as CustomFieldConfig[]
        ).filter((field) => field.fieldType === "disclosure"),
      }))
      .filter((step) => step.fields.length > 0);
  }, [flow, effectiveVisitedPath]);

  const entries = useMemo<AttestationEntry[]>(() => {
    // Latest decision log row per (step, field) for enrichment. Logs come back
    // ordered by created_at asc, so the last write wins.
    const logByKey: Record<string, DisclosureLogRecord> = {};
    logs.forEach((log) => {
      if (!DISCLOSURE_DECISION_EVENT_TYPES.includes(log.eventType)) return;
      const key = `${log.meta?.stepSlug ?? ""}::${log.meta?.fieldKey ?? ""}`;
      logByKey[key] = log;
    });

    const rows: AttestationEntry[] = [];
    disclosuresByStep.forEach(({ stepSlug, fields }) => {
      const response = applicationHelper.findResponse(
        flow.id,
        "draft",
        stepSlug
      );
      const responseFields = response?.fields;
      if (!responseFields) return;
      fields.forEach((field) => {
        // Only a field the applicant was actually shown has a decision; one
        // dropped by a display condition is absent, and "not asked" is not "no".
        if (!(field.fieldKey in responseFields)) return;
        const log = logByKey[`${stepSlug}::${field.fieldKey}`];
        rows.push({
          key: `${stepSlug}::${field.fieldKey}`,
          stepSlug,
          agreed: isTruthy(responseFields[field.fieldKey]),
          fieldText: field.fieldText ?? null,
          // Reduced to plain text because this is rendered as a heading, and
          // the fallback is FI-authored rich text: `fieldLabel` comes from a
          // rich text control, so real configuration carries markup and the
          // tags printed verbatim.
          //
          // The fallback itself is load-bearing and stays. `log.label` is the
          // clean name from the disclosure library, but log writes are
          // fire-and-forget and the list is fetched async — so it is absent on
          // the first render of the review step, and permanently absent if the
          // event was ever dropped. Naming the disclosure only when a log row
          // exists would leave it unnamed exactly when the record is weakest.
          label: toPlainText(log?.label ?? field.fieldLabel) || null,
          createdAt: log?.createdAt ?? null,
          ip: log?.ip ?? null,
          href: disclosureDocumentUrl(
            applicationId,
            field.financialDisclosureSlug
          ),
        });
      });
    });
    return rows;
  }, [disclosuresByStep, logs, applicationHelper, flow.id, applicationId]);

  if (entries.length === 0) {
    return null;
  }

  // IP is normally the same across a session, so show it once beneath the header
  // rather than on every row. Only some rows carry it (a row without a log yet has
  // none), so take the first available.
  const ip = entries.map((entry) => entry.ip).find(Boolean);

  return (
    <Col className={isV2 ? "w-100 pa-3" : "w-100"}>
      {/* Layout mirrors the step review cards above: a circular badge (a
          document icon here, instead of the step checkmark) beside a gray card. */}
      <div className="flex">
        <div
          className="flex flex-column items-center"
          style={{ width: "24px", marginRight: "1rem" }}
        >
          <div
            className="br-100 flex justify-center items-center textPrimary bg-primary"
            style={{
              width: "24px",
              height: "24px",
              minWidth: "24px",
              minHeight: "24px",
              marginTop: "0.5rem",
            }}
          >
            <FileTextOutlined style={{ fontSize: "14px" }} />
          </div>
        </div>
        <div
          className="bg-gray-4 br-5 ph-4 pt-3 pb-4"
          style={{ flex: 1, minWidth: 0 }}
        >
          <h2
            className="fwt f-6"
            style={{ color: "#323333", margin: 0, marginBottom: 4 }}
          >
            Disclosures Summary
          </h2>
          {ip && <p className="mb-3 f-1 gray-7">IP address: {ip}</p>}
          <div className="bg-white br-4 w-100 pa-3">
            {entries.map((entry, idx) => {
              const declined = !entry.agreed;
              return (
                <div
                  key={entry.key}
                  className={idx > 0 ? "mt-3 pt-3 bt b-gray-4" : ""}
                >
                  {/* Disclosure name + a pencil that jumps back to the specific
                      step this decision was made on (disclosures in the summary
                      can span multiple steps). */}
                  <div className="flex justify-between items-center">
                    <p className="mb-1 f-4 fwb" style={{ color: "#323333" }}>
                      {entry.label}
                    </p>
                    <EditFilled
                      className="pointer hlight-primary"
                      style={{ color: "#666", fontSize: "18px" }}
                      onClick={() =>
                        router.push({
                          query: {
                            ...router.query,
                            stepSlug: entry.stepSlug,
                          },
                        })
                      }
                    />
                  </div>
                  {entry.fieldText && (
                    <p
                      className="mb-1 f-2 gray-8"
                      dangerouslySetInnerHTML={{ __html: entry.fieldText }}
                    />
                  )}
                  <p className="mb-0 f-1 gray-7">
                    {/* Lead with the outcome, and label the zone. This block is
                        the screenshot artifact, so a bare local time is ambiguous
                        months later and a bare timestamp says nothing about which
                        way the applicant went. Color on the wrapper, not the icon:
                        antd icons fill with currentColor, so the mark and label
                        stay in sync. */}
                    <span
                      className="fwb"
                      style={{ color: declined ? "#cf1322" : "#52c41a" }}
                    >
                      {declined ? (
                        <CloseCircleFilled className="mr-1" />
                      ) : (
                        <CheckCircleFilled className="mr-1" />
                      )}
                      {declined ? "Did not agree" : "Agreed"}
                    </span>{" "}
                    {entry.createdAt &&
                      dayjs(entry.createdAt).format("MM/DD/YYYY h:mm A z")}
                    {entry.href && (
                      <>
                        {" · "}
                        <a
                          href={entry.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="primary"
                        >
                          View document
                        </a>
                      </>
                    )}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </Col>
  );
}
